import { compact, isEmpty, snakeCase } from "lodash";
import { CASE_SENSITIVE_NETWORKS, Network } from "../network";
import { compareText, toJSON, unique } from "../utils";
export const NFT_ID_SEP = ":";
export const NFT_CACHE_PERIOD = 60000;
export var NFTStandard;
(function (NFTStandard) {
    NFTStandard["ERC721"] = "ERC721";
    NFTStandard["ERC1155"] = "ERC1155";
})(NFTStandard || (NFTStandard = {}));
export const MULTI_OWNER_NFT_STANDARDS = [NFTStandard.ERC1155];
export const toNFTId = (arr) => {
    try {
        return compact(arr).join(NFT_ID_SEP);
    }
    catch (err) {
        console.error(err);
        return "";
    }
};
export const parseNFTId = (id) => {
    let network = "";
    let collectionId = "";
    let tokenId = "";
    try {
        const ids = id.split(NFT_ID_SEP);
        if (ids.length >= 3) {
            network = ids[0];
            collectionId = ids[1];
            tokenId = ids[2];
        }
        else if (ids.length == 2) {
            network = ids[0];
            tokenId = ids[1];
        }
        return { network, collectionId, tokenId };
    }
    catch (err) {
        return { network, collectionId, tokenId };
    }
};
export const toNFTCollectionName = (name) => {
    var _a;
    return (_a = snakeCase(name)) === null || _a === void 0 ? void 0 : _a.toLowerCase();
};
export const normalizeAddress = (network, address) => {
    try {
        if (!address)
            return "";
        if (!network)
            return address;
        return CASE_SENSITIVE_NETWORKS.includes(network)
            ? address
            : address.toLowerCase();
    }
    catch (error) {
        return address;
    }
};
export class NFTCollection {
    constructor(params) {
        var _a;
        this.network = params.network;
        this.collectionId = params.collectionId.toLowerCase();
        this.id = toNFTId([this.network, this.collectionId]);
        this.simplehashId = params.simplehashId;
        this.collectionName = toNFTCollectionName(params.collectionName);
        this.description = params.description || "";
        this.standard = params.standard || this.network.toUpperCase();
        this.nextCursor = params.nextCursor || "";
        this.tokenCount = Number((_a = params.tokenCount) !== null && _a !== void 0 ? _a : 0);
        this.updatedAt = Number(params.updatedAt || new Date().valueOf());
        if (params.tokens) {
            this.tokens = params.tokens;
        }
        else {
            if (this.network == Network.SOLANA)
                this.tokens = [];
        }
    }
    get json() {
        return toJSON(this);
    }
}
export class NFT {
    constructor(params) {
        this.network = params.network;
        this.collectionId = params.collectionId.toLowerCase();
        this.tokenId = normalizeAddress(this.network, params.tokenId);
        this.standard = params.standard
            ? params.standard.toUpperCase()
            : this.network.toUpperCase();
        this.id = toNFTId([this.network, this.collectionId, this.tokenId]);
        this.owners =
            params.owners && !isEmpty(params.owners)
                ? params.owners.map((owner) => (Object.assign(Object.assign({}, owner), { address: normalizeAddress(this.network, owner.address) })))
                : [];
        this.ownerAddresses = unique(this.owners.map((owner) => owner.address));
        this.name = params.name || this.id;
        this.description = params.description || "";
        this.imageUrl = params.imageUrl || "";
        this.videoUrl = params.videoUrl || "";
        this.audioUrl = params.audioUrl || "";
        this.modelUrl = params.modelUrl || "";
        this.marketplaceUrl = params.marketplaceUrl || "";
        this.attributes = params.attributes || [];
        this.updatedAt = Number(params.updatedAt || new Date().valueOf());
        if (params.animationUrl)
            this.animationUrl = params.animationUrl;
        if (params.collection) {
            this.collection = params.collection;
        }
        else if (params.collectionImageUrl) {
            this.collection = { imageUrl: params.collectionImageUrl };
        }
    }
    get json() {
        return toJSON(this);
    }
    update() {
        this.updatedAt = new Date().valueOf();
        return this;
    }
    updateOwners() {
        this.owners = this.owners.filter((owner) => owner.quantity > 0);
        if (!MULTI_OWNER_NFT_STANDARDS.includes(this.standard))
            this.owners = this.owners.map((owner) => ({
                address: owner.address,
                quantity: 1,
            }));
        this.ownerAddresses = unique(this.owners.map((owner) => owner.address));
        return this;
    }
    ownedBy(address) {
        var _a;
        return (((_a = this.owners.find((owner) => compareText(owner.address, address))) === null || _a === void 0 ? void 0 : _a.quantity) || 0);
    }
    transfer(network, from, to, quantity) {
        if (!to)
            return this;
        from = normalizeAddress(network, from || "");
        to = normalizeAddress(network, to || "");
        const toIdx = this.owners.findIndex((owner) => compareText(owner.address, to));
        const fromIdx = this.owners.findIndex((owner) => compareText(owner.address, from));
        if (fromIdx != -1)
            this.owners[fromIdx].quantity -= quantity;
        toIdx == -1
            ? this.owners.push({ address: to, quantity })
            : (this.owners[toIdx].quantity += quantity);
        return this.updateOwners();
    }
    toNFTOwnership(owner) {
        var _a;
        return new NFTOwnership({
            owner,
            network: this.network,
            collectionId: this.collectionId,
            tokenId: this.tokenId,
            standard: this.standard,
            updatedAt: this.updatedAt,
            quantity: ((_a = this.owners.find((_owner) => _owner.address == owner)) === null || _a === void 0 ? void 0 : _a.quantity) || 1,
            attributes: this.attributes,
            imageUrl: this.imageUrl,
        });
    }
}
export class NFTOwnership {
    constructor(params) {
        this.network = params.network;
        this.collectionId = params.collectionId.toLowerCase();
        this.tokenId = normalizeAddress(this.network, params.tokenId);
        this.standard = params.standard
            ? params.standard.toUpperCase()
            : this.network.toUpperCase();
        this.owner = normalizeAddress(this.network, params.owner);
        this.id = toNFTId([this.network, this.collectionId, this.tokenId]);
        this.updatedAt = Number(params.updatedAt || new Date().valueOf());
        this.network = params.network;
        this.quantity = Number(params.quantity || 1);
        this.attributes = params.attributes || [];
        this.imageUrl = params.imageUrl || "";
    }
    get json() {
        return toJSON(this);
    }
    update() {
        this.updatedAt = new Date().valueOf();
    }
}
